import { format, subDays } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { schema } from '@/components/shared/analytics/chart-timeframe'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

export const DateRangeForm = ({ isOpen, onOpenChange }: Props) => {
  const [startCalendarOpen, setStartCalendarOpen] = useState<boolean>(false)
  const [endCalendarOpen, setEndCalendarOpen] = useState<boolean>(false)
  const form = useFormContext<z.infer<typeof schema>>()

  const key = form.watch('key')
  const startDate = form.watch('startDate')
  const endDate = form.watch('endDate')

  const placeholder =
    startDate && endDate
      ? `${format(startDate, 'MMM dd, HH:mm')} - ${format(endDate, 'MMM dd, HH:mm')}`
      : ''

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('rounded-lg rounded-r-none mb-0', key ? '' : 'w-60')}
          title={key ? '' : placeholder}
        >
          {key ? (
            <CalendarIcon />
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full px-0 space-y-4">
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <div className="flex items-end gap-4 px-4">
              <FormItem className="flex flex-col">
                <FormLabel>From</FormLabel>
                <Popover
                  open={startCalendarOpen}
                  onOpenChange={setStartCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-[180px] pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'MMMM dd, yyyy')
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(value) => {
                        if (value) {
                          field.onChange(value)
                          setStartCalendarOpen(false)
                        }
                      }}
                      disabled={(date) =>
                        date > new Date() || date < subDays(new Date(), 60)
                      }
                      captionLayout="dropdown"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
              <FormItem className="flex flex-col">
                <FormControl>
                  <Input
                    value={format(field.value ?? 0, 'HH:mm:ss')}
                    onChange={(e) => {
                      const value = e.target.value
                      const [hours, minutes, seconds] = value?.split?.(':') ?? [
                        '00',
                        '00',
                        '00',
                      ]
                      const startDate = new Date(field.value ?? 0)
                      startDate.setHours(Number(hours ?? '00'))
                      startDate.setMinutes(Number(minutes ?? '00'))
                      startDate.setSeconds(Number(seconds ?? '00'))
                      field.onChange(startDate)
                    }}
                    type="time"
                    step="1"
                    aria-label="Start time"
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <div className="flex items-end gap-4 px-4">
              <FormItem className="flex flex-col">
                <FormLabel>To</FormLabel>
                <Popover
                  open={endCalendarOpen}
                  onOpenChange={setEndCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-[180px] pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'MMMM dd, yyyy')
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(value) => {
                        if (value) {
                          field.onChange(value)
                          setEndCalendarOpen(false)
                        }
                      }}
                      disabled={(date) => date < startDate || date > new Date()}
                      captionLayout="dropdown"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
              <FormItem className="flex flex-col">
                <FormControl>
                  <Input
                    value={format(field.value ?? 0, 'HH:mm:ss')}
                    onChange={(e) => {
                      const value = e.target.value
                      const [hours, minutes, seconds] = value?.split?.(':') ?? [
                        '00',
                        '00',
                        '00',
                      ]
                      const startDate = new Date(field.value ?? 0)
                      startDate.setHours(Number(hours ?? '00'))
                      startDate.setMinutes(Number(minutes ?? '00'))
                      startDate.setSeconds(Number(seconds ?? '00'))
                      field.onChange(startDate)
                    }}
                    type="time"
                    step="1"
                    aria-label="End time"
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />
        <Separator />
        <div className="flex justify-end px-4 gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="chart-timeframe-form"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
